import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Download, Share } from "lucide-react";

/**
 * FoodPulse "Add to Home Screen" prompt.
 * - Captures the `beforeinstallprompt` event on Android/Chrome and shows a
 *   friendly banner instead of the default browser mini-infobar.
 * - Falls back to a Safari-specific instruction card for iOS (which has no
 *   programmatic install API — user must tap Share → Add to Home Screen).
 * - Persists dismissal for 30 days per browser via localStorage.
 * - Only renders on `/food-pulse*` routes (or when explicitly enabled) so
 *   SocialPulse / IFT visitors are not nagged.
 */
const DISMISS_KEY = "fp_pwa_dismissed_until";
const DISMISS_DAYS = 30;

function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari legacy check
    window.navigator.standalone === true
  );
}

function dismissedRecently() {
  try {
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    return Date.now() < until;
  } catch {
    return false;
  }
}

function markDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 86400 * 1000));
  } catch {
    // ignore
  }
}

export default function PwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || dismissedRecently()) return;
    if (typeof window === "undefined") return;

    // Only prompt on FoodPulse routes.
    const path = window.location.pathname;
    const onFoodPulse = /^\/(food-pulse|basket|pantry|receipts|smart-basket|m\/)/.test(path) || path === "/food-pulse";
    if (!onFoodPulse) return;

    const onBip = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    // iOS Safari has no beforeinstallprompt — show the manual hint instead.
    if (isIos()) {
      setIosHint(true);
      setVisible(true);
    }

    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  const dismiss = () => {
    markDismissed();
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {
      // ignore
    }
    setDeferredPrompt(null);
    setVisible(false);
    markDismissed();
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-1/2 z-[9500] w-[92%] max-w-md -translate-x-1/2 rounded-2xl border border-emerald-200 bg-white p-4 shadow-2xl"
      role="dialog"
      aria-labelledby="pwa-title"
    >
      <button
        data-testid="pwa-install-dismiss"
        onClick={dismiss}
        className="absolute right-2 top-2 rounded-full p-1 text-slate-400 hover:bg-slate-100"
        aria-label="Dismiss install prompt"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="grid h-11 w-11 shrink-0 place-content-center overflow-hidden rounded-xl bg-emerald-50">
          <img src="/foodpulse-icon-192.png" alt="FoodPulse" className="h-9 w-9" />
        </div>
        <div className="flex-1">
          <p id="pwa-title" className="text-base font-semibold text-slate-900">
            Install FoodPulse
          </p>
          {iosHint ? (
            <p className="mt-1 text-sm text-slate-600">
              Tap <Share className="inline h-3.5 w-3.5" /> <span className="font-medium">Share</span> in Safari, then
              <span className="font-medium"> Add to Home Screen</span> to install.
            </p>
          ) : (
            <p className="mt-1 text-sm text-slate-600">
              Add FoodPulse to your home screen for one-tap access to Deals, Scan and Pantry — even offline.
            </p>
          )}
          {!iosHint && (
            <div className="mt-3 flex gap-2">
              <Button
                data-testid="pwa-install-btn"
                size="sm"
                onClick={install}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Download className="mr-1.5 h-4 w-4" /> Install
              </Button>
              <Button
                data-testid="pwa-install-later"
                size="sm"
                variant="ghost"
                onClick={dismiss}
              >
                Not now
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
